import { GridColDef } from '@mui/x-data-grid-premium';
import React from 'react';
import { tableSettingsProps, useTableSettings } from 'src/core/tables/useTableSettings';
import tableHelper from 'src/core/tables/tableHelper';
import { useGetAllCampaigns } from 'src/pages/orders/orderRequests';

export const useCampaignsTable = (): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const { rows, status } = useGetAllCampaigns();
  const idPath = ['Id'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'actions', getActions: (params) => tableHelper.buttonColumn(params, 'Edit', '/orders/campaigns/edit?id=', 'Id', 'Edit Campaign'), type: 'actions', headerName: 'View', minWidth: 80, maxWidth: 120, flex: 0.8 },
      { field: 'Id', headerName: 'Campaign Id' },
      { field: 'Name', headerName: 'Campaign Name', flex: 1, minWidth: 200 },
      { field: 'Type_Value', headerName: 'Campaign Type', valueGetter: tableHelper.getFieldKeyOrValue, minWidth: 180 },
      { field: 'Status_Value', headerName: 'Status', valueGetter: tableHelper.getFieldKeyOrValue, renderCell: tableHelper.renderStatusCell, minWidth: 150 },
      { field: 'ValidFrom', headerName: 'Valid From', type: 'dateTime', valueGetter: tableHelper.getDateColumn, minWidth: 170 },
      { field: 'ValidTo', headerName: 'Valid To', type: 'dateTime', valueGetter: tableHelper.getDateColumn, minWidth: 170 },
      { field: 'SalesItemsCount', headerName: 'Number of Sales Items', type: 'number', minWidth: 200 }
    ],
    []
  );
  const tableName = 'CampaignsTable';
  const tableSettings = useTableSettings({ tableName, idPath, columns, rows, status });
  return [columns, tableSettings];
};

export const useSalesPriceListTable = (initialValues: any): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const rows = initialValues?.SalesPriceLists ?? EMPTY_ROWS;
  const idPath = ['SalesPriceList', 'Key'];
  const tableName = 'SalesPriceListsTable';
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'SalesPriceList_Key', headerName: 'Sales Price List ID', flex: 1, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'Organisation_Value', headerName: 'Organisation', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'OrganisationAssortment_Value', headerName: 'Organisation Assortment', width: 230, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SalesAssortment_Value', headerName: 'Sales Assortment', width: 210, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SalesPriceList_Value', headerName: 'Sales Price List', width: 210, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SalesPriceListCurrencyName', headerName: 'Currency', width: 120 },
      { field: 'SalesPriceListTaxIncluded', headerName: 'Tax Included', width: 140, type: 'boolean' }
    ],
    []
  );
  const tableSettings = useTableSettings({ tableName, columns, rows, idPath });
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;
  return [columns, tableSettings];
};
export const useCampaignSalesItemsTable = (initialValues: any): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const rows = initialValues?.CampaignSalesItems ?? EMPTY_ROWS;
  const tableName = 'CampaignSalesItemsTable';
  const idPath = ['SalesItemId'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'SalesItemId', headerName: 'Sales Item ID', width: 140 },
      { field: 'SalesItemNumber', headerName: 'Article Number', width: 160 },
      { field: 'SalesItemName', headerName: 'Name', flex: 1, minWidth: 220 },
      { field: 'ManufacturerName', headerName: 'Brand', width: 160 },
      { field: 'ManufacturerProductId', headerName: 'Manufacturer Product ID', width: 220 },
      { field: 'ProductType', headerName: 'Product Type', width: 160 },
      { field: 'ProductLine', headerName: 'Product Line', width: 160 },
      { field: 'HideStandardPrice', headerName: 'Hide Std Price', type: 'boolean', width: 150 },
      { field: 'MaxQuantityPerOrder', headerName: 'Max / Order', width: 130 },
      { field: 'MaxQuantityPerCustomer', headerName: 'Max / Customer', width: 150 }
    ],
    []
  );
  const tableSettings = useTableSettings({ tableName, columns, rows, idPath });
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;
  return [columns, tableSettings];
};
export const useCampaignPricesTable = (initialValues: any): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const rows = initialValues?.CampaignPrices ?? EMPTY_ROWS;
  const tableName = 'CampaignPricesTable';
  const idPath = ['ArticleNumber'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'ArticleNumber', headerName: 'Article Number', width: 160 },
      { field: 'Name', headerName: 'Name', flex: 1, minWidth: 240 },
      { field: 'Manufacturer', headerName: 'Brand', width: 140 },
      { field: 'ProductType', headerName: 'Product Type', width: 160 },
      { field: 'SalesPriceList_Value', headerName: 'Sales Price List', width: 200, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'OriginalPrice', headerName: 'Original Price', width: 150 },
      { field: 'CampaignPrice', headerName: 'Campaign Price', width: 160 },
      { field: 'DiscountType_Value', headerName: 'Discount Type', width: 160, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'DiscountValue', headerName: 'Discount Value', width: 140 },
      { field: 'DiscountBasedOn', headerName: 'Based On', width: 140 },
      { field: 'Margin', headerName: 'Margin %', width: 120 },
      { field: 'Disabled', headerName: 'Disabled', type: 'boolean', width: 120 },
      { field: 'Status', headerName: 'Status', width: 110 }
    ],
    []
  );
  const tableSettings = useTableSettings({ tableName, columns, rows, idPath });
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;
  return [columns, tableSettings];
};
export const useCampaignSalesRuleTable = (initialValues: any): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const rows = initialValues?.CampaignSalesRule ? [initialValues.CampaignSalesRule] : EMPTY_ROWS;
  const tableName = 'CampaignSalesRuleTable';
  const idPath = ['Id'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'Id', headerName: 'Rule ID', width: 120 },
      { field: 'Prio', headerName: 'Priority', width: 120 },
      { field: 'OrderDiscount', headerName: 'Order Discount', width: 150 },
      { field: 'OrderDiscountType_Value', headerName: 'Discount Type', width: 160, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'MinimumAmountGross', headerName: 'Min Order Amount', width: 170 },
      { field: 'MinimumQuantity', headerName: 'Min Quantity', width: 150 },
      { field: 'FreeArticleSalesItemId', headerName: 'Free Article ID', width: 160 },
      { field: 'FreeArticleSalesItemName', headerName: 'Free Article Name', flex: 1, minWidth: 240 },
      { field: 'FreeQuantity', headerName: 'Free Qty', width: 120 }
    ],
    []
  );
  const tableSettings = useTableSettings({ tableName, columns, rows, idPath });
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;
  return [columns, tableSettings];
};

const EMPTY_ROWS: any[] = [];
